import { useState, useEffect } from 'react'
import { Grid, Loading } from 'react-vant'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import teacherAffairsApi from '../../../api/teacherAffairs'

const STAT_CARDS = [
  { key: 'duty', label: '值班次数', emoji: '📅', color: '#056b4f' },
  { key: 'overtime', label: '加班时长', emoji: '⏰', color: '#00b578' },
  { key: 'leave', label: '请假天数', emoji: '📝', color: '#fa8c16' },
  { key: 'balance', label: '免假余额', emoji: '🛡️', color: '#7c3aed' },
]

const QUICK_ACTIONS = [
  { emoji: '📅', text: '我的值班', path: '/teacher/affairs/duty', color: '#056b4f' },
  { emoji: '⏰', text: '我的加班', path: '/teacher/affairs/overtime', color: '#00b578' },
  { emoji: '📝', text: '我的请假', path: '/teacher/affairs/leave', color: '#fa8c16' },
  { emoji: '🔁', text: '我的代课', path: '/teacher/affairs/substitute', color: '#0891b2' },
]

// admin 看到的统计卡：全校待办与概览
const ADMIN_STAT_CARDS = [
  { key: 'pending_leave_count', label: '待审批请假', emoji: '✅', color: '#fa8c16' },
  { key: 'pending_substitute_count', label: '待安排代课', emoji: '🔁', color: '#0891b2' },
  { key: 'overtime_hours_month', label: '本月加班(h)', emoji: '⏰', color: '#00b578' },
  { key: 'leave_days_month', label: '本月请假(天)', emoji: '📝', color: '#7c3aed' },
]

// admin 是纯管理账号，没有个人事务身份 → 换成全局管理入口
const ADMIN_ACTIONS = [
  { emoji: '✅', text: '待审批请假', path: '/teacher/affairs/admin/pending-leave', color: '#fa8c16' },
  { emoji: '🔁', text: '待安排代课', path: '/teacher/affairs/admin/pending-substitute', color: '#0891b2' },
  { emoji: '📊', text: '事务总览', path: '/teacher/affairs/admin/overview', color: '#056b4f' },
]

export default function AffairsDashboard() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)
  // admin 是纯管理账号 → 本页展示全局管理视角，不展示个人事务
  const isAdminUser = userInfo?.role === 'admin'
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    duty: 0, overtime: 0, leave: 0, balance: 0
  })

  useEffect(() => {
    fetchDashboardData()
  }, [])

  // admin 拉管理速览
  useEffect(() => {
    if (!isAdminUser) return
    teacherAffairsApi.getAdminOverview()
      .then(res => setOverview(res?.data || res || null))
      .catch(() => {})
  }, [isAdminUser])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const now = new Date()
      const yearStart = `${now.getFullYear()}-01-01`
      const yearEnd = `${now.getFullYear()}-12-31`

      const [dutyRes, otRes, leaveRes, balanceRes] = await Promise.allSettled([
        teacherAffairsApi.getDutySchedules({
          teacher_id: userInfo?.id,
          start_date: yearStart,
          end_date: yearEnd
        }),
        teacherAffairsApi.getOvertimeStats({ start_date: yearStart, end_date: yearEnd }),
        teacherAffairsApi.getLeaveStats({ start_date: yearStart, end_date: yearEnd }),
        userInfo?.id ? teacherAffairsApi.getLeaveBalance(userInfo.id) : Promise.resolve([]),
      ])

      const dutyCount = dutyRes.status === 'fulfilled' ? (dutyRes.value?.length || 0) : 0
      const otHours = otRes.status === 'fulfilled' ? (otRes.value?.total_hours || 0) : 0
      const leaveDays = leaveRes.status === 'fulfilled' ? (leaveRes.value?.total_days || 0) : 0
      const balances = balanceRes.status === 'fulfilled' ? (balanceRes.value || []) : []
      const totalBalance = Array.isArray(balances)
        ? balances.reduce((sum, b) => sum + (b.remaining_days || 0), 0)
        : 0

      setStats({
        duty: dutyCount,
        overtime: Math.round(otHours * 10) / 10,
        leave: Math.round(leaveDays * 10) / 10,
        balance: Math.round(totalBalance * 10) / 10,
      })
    } catch (err) {
      console.error('加载仪表盘数据失败:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ background: '#f5f8f6', minHeight: '100vh' }}>
      {/* 自定义顶部导航（不使用 react-vant NavBar） */}
      <div style={{
        background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
        padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div onClick={() => navigate(-1)} style={{ cursor: 'pointer', padding: 4 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>教师事务</span>
        <div style={{ width: 24 }} />
      </div>

      {/* Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
        padding: '0 20px 28px',
        color: '#fff',
        borderRadius: '0 0 24px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-30%', right: '-15%', width: '60%', height: '150%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }} />
        <p style={{ margin: 0, fontSize: 13, opacity: 0.85, position: 'relative', zIndex: 1 }}>
          {isAdminUser ? '全校事务 · 待办审批 · 代课安排' : '值班管理 · 加班统计 · 请假管理 · 免假额度'}
        </p>
      </div>

      {/* 统计卡片 */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
        margin: '-16px 12px 0', position: 'relative', zIndex: 10,
      }}>
        {(isAdminUser ? ADMIN_STAT_CARDS : STAT_CARDS).map(card => (
          <div key={card.key} style={{
            background: '#fff', borderRadius: 14, padding: '14px 8px',
            textAlign: 'center',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          }}>
            <span style={{ fontSize: 22 }}>{card.emoji}</span>
            <div style={{
              fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 6,
            }}>
              {loading ? <Loading size={16} />
                : (isAdminUser ? (overview?.[card.key] ?? 0) : stats[card.key])}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* 功能入口 */}
      <div style={{
        margin: '20px 12px', background: '#fff', borderRadius: 16,
        padding: '20px 12px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', marginBottom: 16, paddingLeft: 4,
        }}>
          <div style={{
            width: 4, height: 16, background: '#7c3aed', borderRadius: 2, marginRight: 8,
          }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
            {isAdminUser ? '管理工作台' : '个人工作台'}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
          {(isAdminUser ? ADMIN_ACTIONS : QUICK_ACTIONS).map(action => (
            <div
              key={action.path}
              onClick={() => navigate(action.path)}
              style={{ padding: '12px 0', textAlign: 'center', cursor: 'pointer' }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: `${action.color}12`, display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
              }}>
                <span style={{ fontSize: 26 }}>{action.emoji}</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>
                {action.text}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 小贴士 */}
      <div style={{
        margin: '0 12px 20px', background: '#fff', borderRadius: 16,
        padding: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      }}>
        <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.8 }}>
          💡 <strong>温馨提示</strong>
          {isAdminUser ? (
            <>
              <br />· 管理员账号不含个人事务，请用教师账号提交请假/加班
              <br />· 审批通过后系统会自动识别受影响课时并进入「待安排」
              <br />· 代课指派后请通知代课教师本人
            </>
          ) : (
            <>
              <br />· 值班报名请在窗口截止前完成
              <br />· 加班记录需在 7 天内提交
              <br />· 病假超过 1 天需上传挂号凭证
            </>
          )}
        </div>
      </div>
    </div>
  )
}
